import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { CanvasFrame, ErrorScreen } from './error-screen';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Last line of defence, above the router and the providers.
 *
 * It sits outside everything on purpose: a crash inside a provider would leave
 * a route-level boundary with nothing to render into. Resetting keeps the tab
 * alive, so an accidental crash does not cost the person their unsaved work in
 * another tab or their session.
 */
export class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Fallo no controlado en la raíz de Ribox CRM', error, info.componentStack);
  }

  private readonly reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <CanvasFrame>
        <ErrorScreen
          title="La aplicación se cayó"
          description="Algo falló fuera de toda pantalla. Puedes reintentar sin perder la sesión, o recargar de cero."
          error={error}
          onRetry={this.reset}
          action={
            <Button variant="secondary" onClick={() => window.location.reload()}>
              Recargar la app
            </Button>
          }
        />
      </CanvasFrame>
    );
  }
}
